import axios, { AxiosRequestConfig, AxiosResponse, AxiosError } from "axios";
import { doApiCall } from "./utils";

const {
  REACT_APP_ORDER_MEMBER_PORTAL_ENDPOINT_URL,
  REACT_APP_RISK_API_ENDPOINT_URL
} = process.env;

interface Finding {
  variant: string;
  transcript: string;
  variantClassification: string;
  zygosity: string;
  gene: string;
}

interface RiskAssessment {
  updatedOn: string;
  glOrderId: string;
  findings: Finding[];
  glDocumentId: string;
  topLineSummary: string[];
  glMemberId: string;
  topLineResult: string;
}

interface RiskAssessmentResponse extends Array<RiskAssessment> {}

type TestKitEligibilityEntry = { testId: string };

interface TestKitsEligibilityResponse {
  ghs?: TestKitEligibilityEntry;
  pgx?: TestKitEligibilityEntry;
  carrier?: TestKitEligibilityEntry;
}

/**
 * Get all the user orders
 * @returns Promise containing the orders
 */
export const getExistingKitOrders = async (): Promise<
  AxiosResponse | AxiosError
> => {
  const request: AxiosRequestConfig = {
    method: "GET",
    url: `https://${REACT_APP_ORDER_MEMBER_PORTAL_ENDPOINT_URL}/orders`,
    responseType: "json"
  };

  const response = await doApiCall(request);
  if (response instanceof AxiosError) {
    throw response;
  }
  return doApiCall(request);
};

/**
 * Download a document for a specific order
 * @param orderId The ID of the order
 * @param documentId The ID of the document to download
 * @returns Promise containing the document data
 */
export const downloadOrderDocuments = async (
  orderId: string,
  documentId: string
): Promise<AxiosResponse | AxiosError> => {
  const request: AxiosRequestConfig = {
    method: "GET",
    url: `https://${REACT_APP_ORDER_MEMBER_PORTAL_ENDPOINT_URL}/orders/${orderId}/documents/${documentId}`
  };

  return doApiCall(request);
};

/**
 * Download a document for a specific order with PDF response type
 * @param orderId The ID of the order
 * @param documentId The ID of the document to download
 * @returns Promise containing the document data
 */
export const downloadOrderDocument = async (
  orderId: string,
  documentId: string
): Promise<AxiosResponse | AxiosError> => {
  const request: AxiosRequestConfig = {
    method: "GET",
    url: `https://${REACT_APP_ORDER_MEMBER_PORTAL_ENDPOINT_URL}/orders/${orderId}/documents/${documentId}`,
    responseType: "blob"
  };

  return doApiCall(request);
};

/**
 * Get a document by its ID
 * @param documentId The ID of the document to retrieve
 * @returns Promise containing the document data
 */
export const getDocument = async (
  documentId: string
): Promise<AxiosResponse | AxiosError> => {
  const request: AxiosRequestConfig = {
    method: "GET",
    url: `https://${REACT_APP_ORDER_MEMBER_PORTAL_ENDPOINT_URL}/documents/${documentId}`,
    responseType: "json"
  };

  return doApiCall(request);
};

/**
 * Get the risk assessment for a specific order
 * @returns Promise containing the risk assessment data
 */
export const getRiskAssessment = async (): Promise<
  AxiosResponse<RiskAssessmentResponse> | AxiosError
> => {
  const request: AxiosRequestConfig = {
    method: "GET",
    url: `https://${REACT_APP_RISK_API_ENDPOINT_URL}`,
    responseType: "json"
  };

  return doApiCall(request);
};

/**
 * Get test-kit eligibility / catalog IDs for each test type.
 * @returns Promise containing `{ ghs?, pgx?, carrier? }` with `testId` per type
 */
export const getTestKitIdForEachTestType = async (): Promise<
  AxiosResponse<TestKitsEligibilityResponse> | AxiosError
> => {
  const request: AxiosRequestConfig = {
    method: "GET",
    url: `https://${REACT_APP_ORDER_MEMBER_PORTAL_ENDPOINT_URL}/test-kits/eligibility`,
    responseType: "json"
  };

  return doApiCall(request);
};

export type CreateTestRedirectRequest = {
  firstName: string;
  lastName: string;
  testId: string;
};

export type MemberTestKitRedirectEligibility = {
  isEligible: boolean;
};

export type MemberTestKitRedirectEligibilities = Record<
  string,
  MemberTestKitRedirectEligibility
>;

/**
 * Which test redirects this member is entitled to start.
 * Keys are test types, e.g. `{ cologuard: { isEligible: true } }`.
 * A 403 means the member is not entitled to any redirect; treat that as `{}`
 * so login scrape can continue.
 */
export const getTestRedirectEligibilities = async (): Promise<
  AxiosResponse<MemberTestKitRedirectEligibilities> | AxiosError
> => {
  const request: AxiosRequestConfig = {
    method: "GET",
    url: `https://${REACT_APP_ORDER_MEMBER_PORTAL_ENDPOINT_URL}/test-redirects/eligibility`,
    responseType: "json"
  };

  const response = await doApiCall(request);
  if (axios.isAxiosError(response) && response.response?.status === 403) {
    return {
      data: {},
      status: 200,
      statusText: "OK",
      headers: {},
      config: response.config
    } as AxiosResponse<MemberTestKitRedirectEligibilities>;
  }

  return response as
    | AxiosResponse<MemberTestKitRedirectEligibilities>
    | AxiosError;
};

export const createTestRedirect = (
  payload: CreateTestRedirectRequest
): Promise<AxiosResponse | AxiosError> => {
  const request: AxiosRequestConfig = {
    method: "POST",
    url: `https://${REACT_APP_ORDER_MEMBER_PORTAL_ENDPOINT_URL}/test-redirects`,
    data: payload
  };

  return doApiCall(request);
};
